/**
 * useCommandAutocomplete - Command Parsing and Autocomplete Hook
 * 
 * Extracts command autocomplete logic from WorkspacePage.tsx:
 * - @open, @edit, @close, @plan, @ref, @context command detection
 * - Autocomplete visibility state management
 * - Query state for filtering
 * - Close all autocomplete utility
 *
 * Internally uses a single active-command model (2 state vars) instead of
 * 20 individual show/query pairs. The return type is unchanged — consumers
 * see the same AutocompleteState objects and setter functions.
 */

import { useState, useCallback, useMemo } from 'react';

interface CommandParserFns {
  isTypingCommand: (input: string, command: string) => boolean;
  extractQuery: (input: string, command: string) => string;
}

interface UseCommandAutocompleteDeps {
  setInput: (value: string) => void;
  commandParser: CommandParserFns;
}

interface AutocompleteState {
  show: boolean;
  query: string;
}

type CommandType = 'menu' | 'command' | 'edit' | 'close' | 'plan' | 'ref' | 'context' | 'project' | 'go' | 'block';

interface UseCommandAutocompleteReturn {
  // Command menu (shows when user types just @)
  commandMenu: AutocompleteState;
  
  // Individual autocomplete states
  command: AutocompleteState;
  edit: AutocompleteState;
  close: AutocompleteState;
  plan: AutocompleteState;
  ref: AutocompleteState;
  context: AutocompleteState;
  project: AutocompleteState;
  go: AutocompleteState;
  block: AutocompleteState;
  
  // Setters for visibility
  setShowCommandMenu: (show: boolean) => void;
  setShowCommandAutocomplete: (show: boolean) => void;
  setShowEditAutocomplete: (show: boolean) => void;
  setShowCloseAutocomplete: (show: boolean) => void;
  setShowPlanAutocomplete: (show: boolean) => void;
  setShowRefAutocomplete: (show: boolean) => void;
  setShowContextAutocomplete: (show: boolean) => void;
  setShowProjectAutocomplete: (show: boolean) => void;
  setShowGoAutocomplete: (show: boolean) => void;
  setShowBlockAutocomplete: (show: boolean) => void;
  
  // Setters for queries
  setCommandMenuQuery: (query: string) => void;
  setCommandQuery: (query: string) => void;
  setEditQuery: (query: string) => void;
  setCloseQuery: (query: string) => void;
  setPlanQuery: (query: string) => void;
  setRefQuery: (query: string) => void;
  setContextQuery: (query: string) => void;
  setProjectQuery: (query: string) => void;
  setGoQuery: (query: string) => void;
  setBlockQuery: (query: string) => void;
  
  // Main handler for input changes
  handleCommandInput: (value: string) => void;
  
  // Utility to close all autocomplete menus
  closeAllAutocomplete: () => void;
}

export function useCommandAutocomplete(deps: UseCommandAutocompleteDeps): UseCommandAutocompleteReturn {
  const { setInput, commandParser } = deps;
  const { isTypingCommand, extractQuery } = commandParser;

  const [activeCommand, setActiveCommand] = useState<CommandType | null>(null);
  const [query, setQuery] = useState('');

  // Computed autocomplete states
  const stateFor = (type: CommandType): AutocompleteState => ({
    show: activeCommand === type,
    query: activeCommand === type ? query : '',
  });

  const closeAllAutocomplete = useCallback(() => {
    setActiveCommand(null);
    setQuery('');
  }, []);

  const activate = useCallback((type: CommandType, q: string) => {
    setActiveCommand(type);
    setQuery(q);
  }, []);

  const handleCommandInput = useCallback((value: string) => {
    setInput(value);

    if (isTypingCommand(value, 'open')) {
      activate('command', extractQuery(value, 'open'));
    } else if (isTypingCommand(value, 'edit')) {
      activate('edit', extractQuery(value, 'edit'));
    } else if (isTypingCommand(value, 'close')) {
      activate('close', extractQuery(value, 'close'));
    } else if (isTypingCommand(value, 'plan')) {
      activate('plan', extractQuery(value, 'plan'));
    } else if (isTypingCommand(value, 'ref') || isTypingCommand(value, 'references')) {
      activate('ref', extractQuery(value, 'ref') || extractQuery(value, 'references'));
    } else if (isTypingCommand(value, 'context')) {
      activate('context', extractQuery(value, 'context'));
    } else if (isTypingCommand(value, 'project')) {
      activate('project', extractQuery(value, 'project'));
    } else if (isTypingCommand(value, 'go') || isTypingCommand(value, 'goto') || isTypingCommand(value, 'navigate')) {
      activate('go', extractQuery(value, 'go') || extractQuery(value, 'goto') || extractQuery(value, 'navigate'));
    } else if (isTypingCommand(value, 'block')) {
      activate('block', extractQuery(value, 'block'));
    } else {
      const atMatch = value.match(/@(\w*)$/);
      if (atMatch) {
        activate('menu', atMatch[1] || '');
      } else {
        closeAllAutocomplete();
      }
    }
  }, [setInput, isTypingCommand, extractQuery, activate, closeAllAutocomplete]);

  // Visibility setters — compatible with the old per-command boolean API.
  // Setting true activates that command (deactivating any other).
  // Setting false deactivates only if that command is currently active.
  const makeShowSetter = useCallback((type: CommandType) => {
    return (show: boolean) => {
      if (show) {
        setActiveCommand(type);
        setQuery('');
      } else if (activeCommand === type) {
        setActiveCommand(null);
        setQuery('');
      }
    };
  }, [activeCommand]);

  // Query setters — update the shared query (only meaningful when matching command is active)
  const setQueryAlias = useCallback((q: string) => setQuery(q), []);

  // Memoize setters that depend on activeCommand so they stay referentially stable
  // within a render but update when activeCommand changes.
  const showSetters = useMemo(() => ({
    setShowCommandMenu: makeShowSetter('menu'),
    setShowCommandAutocomplete: makeShowSetter('command'),
    setShowEditAutocomplete: makeShowSetter('edit'),
    setShowCloseAutocomplete: makeShowSetter('close'),
    setShowPlanAutocomplete: makeShowSetter('plan'),
    setShowRefAutocomplete: makeShowSetter('ref'),
    setShowContextAutocomplete: makeShowSetter('context'),
    setShowProjectAutocomplete: makeShowSetter('project'),
    setShowGoAutocomplete: makeShowSetter('go'),
    setShowBlockAutocomplete: makeShowSetter('block'),
  }), [makeShowSetter]);

  return {
    commandMenu: stateFor('menu'),
    command: stateFor('command'),
    edit: stateFor('edit'),
    close: stateFor('close'),
    plan: stateFor('plan'),
    ref: stateFor('ref'),
    context: stateFor('context'),
    project: stateFor('project'),
    go: stateFor('go'),
    block: stateFor('block'),

    ...showSetters,

    setCommandMenuQuery: setQueryAlias,
    setCommandQuery: setQueryAlias,
    setEditQuery: setQueryAlias,
    setCloseQuery: setQueryAlias,
    setPlanQuery: setQueryAlias,
    setRefQuery: setQueryAlias,
    setContextQuery: setQueryAlias,
    setProjectQuery: setQueryAlias,
    setGoQuery: setQueryAlias,
    setBlockQuery: setQueryAlias,

    handleCommandInput,
    closeAllAutocomplete,
  };
}
